import { useCallback, useMemo, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { uploadReceiptFile, RECEIPT_UPLOAD_CONCURRENCY } from 'src/services/receipts/api'
import { validateReceiptFiles, type RejectionReason } from 'src/services/receipts/upload-limits'
import { mapWithConcurrency } from 'src/utils/concurrency'
import type { Receipt } from 'src/services/receipts/models'

export type UploadStatus = 'pending' | 'uploading' | 'uploaded' | 'failed' | 'rejected'

/**
 * A failed upload's reason, in a form the UI localizes at render time.
 * - `raw` carries human text the server (or an Error) already produced; it is
 *   shown as-is since the client can't translate an arbitrary server message.
 * - `status` carries only an HTTP status — the UI builds a localized string
 *   around it.
 * - `generic` has nothing useful; the UI shows a localized generic message.
 */
export type UploadError =
  | { kind: 'raw'; message: string }
  | { kind: 'status'; status: number }
  | { kind: 'generic' }

export interface ReceiptUploadItem {
  /** Stable client-side id, independent of array position. */
  id: string
  file: File
  /** Editable display name (defaults to the filename sans extension). */
  name: string
  originalName: string
  progress: number
  status: UploadStatus
  /** Failure reason, set only when status is 'failed'; localized at render. */
  error?: UploadError
  /** Why the file was turned away before upload, set only when status is 'rejected'. */
  rejectionReason?: RejectionReason
  receipt?: Receipt
}

/**
 * Turn a request failure into a reason for the file row. Prefers the server's
 * own message, falls back to the HTTP status, then to a generic marker — never
 * surfaces a raw stack or "[object Object]". The UI localizes the status and
 * generic cases; a `raw` server message is shown verbatim.
 */
export const describeUploadError = (err: unknown): UploadError => {
  if (isAxiosError(err)) {
    const data = err.response?.data as unknown
    if (typeof data === 'string' && data.trim()) return { kind: 'raw', message: data }
    if (data && typeof data === 'object') {
      const detail =
        (data as Record<string, unknown>).detail ?? (data as Record<string, unknown>).asset
      if (typeof detail === 'string' && detail.trim()) return { kind: 'raw', message: detail }
      if (Array.isArray(detail) && typeof detail[0] === 'string')
        return { kind: 'raw', message: detail[0] }
    }
    if (err.response?.status) return { kind: 'status', status: err.response.status }
  }
  if (err instanceof Error && err.message) return { kind: 'raw', message: err.message }
  return { kind: 'generic' }
}

const stageFile = (file: File, id: string): ReceiptUploadItem => ({
  id,
  file,
  name: file.name.replace(/\.[^/.]+$/, ''),
  originalName: file.name,
  progress: 0,
  status: 'pending',
})

const stageRejected = (file: File, id: string, reason: RejectionReason): ReceiptUploadItem => ({
  ...stageFile(file, id),
  status: 'rejected',
  rejectionReason: reason,
})

/**
 * Owns the per-file upload lifecycle for a bulk receipt upload: each staged
 * file carries its own progress (0→100) and resolves to an independent terminal
 * state (uploaded / failed-with-reason), and a failed file can be retried on its
 * own without touching the ones that already succeeded.
 *
 * Progress comes entirely from the browser's upload-progress events via
 * `uploadReceiptFile` — no backend job, websocket, or polling.
 */
export const useReceiptUploads = () => {
  const [items, setItems] = useState<ReceiptUploadItem[]>([])
  const nextId = useRef(0)

  const patch = useCallback((id: string, changes: Partial<ReceiptUploadItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)))
  }, [])

  const addFiles = useCallback((files: File[]) => {
    setItems((prev) => {
      // Count cap applies to the running total of files that could still
      // upload, so already-rejected rows don't consume a slot.
      const stagedCount = prev.filter((it) => it.status !== 'rejected').length
      const { accepted, rejected } = validateReceiptFiles(files, stagedCount)
      return [
        ...prev,
        ...accepted.map((file) => stageFile(file, `upload-${nextId.current++}`)),
        ...rejected.map(({ file, reason }) =>
          stageRejected(file, `upload-${nextId.current++}`, reason),
        ),
      ]
    })
  }, [])

  const rename = useCallback((id: string, name: string) => patch(id, { name }), [patch])

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id))
  }, [])

  const reset = useCallback(() => setItems([]), [])

  /** Upload one staged/failed file, driving its progress and terminal state. */
  const uploadOne = useCallback(
    async (item: ReceiptUploadItem): Promise<Receipt | null> => {
      patch(item.id, { status: 'uploading', progress: 0, error: undefined })
      try {
        const receipt = await uploadReceiptFile(
          { name: item.name, asset: item.file },
          { onUploadProgress: (percent) => patch(item.id, { progress: percent }) },
        )
        patch(item.id, { status: 'uploaded', progress: 100, receipt })
        return receipt
      } catch (err) {
        patch(item.id, { status: 'failed', error: describeUploadError(err) })
        return null
      }
    },
    [patch],
  )

  /**
   * Upload every file that hasn't succeeded yet, bounded by the concurrency cap.
   * Already-uploaded files are skipped, so this doubles as "retry all failed".
   * Resolves with the list of created Receipts.
   */
  const uploadAll = useCallback(async (): Promise<Receipt[]> => {
    // Only files that can still upload — never the already-uploaded ones, and
    // never files rejected by the client-side limits.
    const pending = items.filter((it) => it.status === 'pending' || it.status === 'failed')
    const settled = await mapWithConcurrency(pending, RECEIPT_UPLOAD_CONCURRENCY, uploadOne)
    return settled.flatMap((res) => (res.status === 'fulfilled' && res.value ? [res.value] : []))
  }, [items, uploadOne])

  /** Retry a single failed file without re-uploading the ones that succeeded. */
  const retry = useCallback(
    (id: string): Promise<Receipt | null> => {
      const item = items.find((it) => it.id === id)
      return item ? uploadOne(item) : Promise.resolve(null)
    },
    [items, uploadOne],
  )

  const counts = useMemo(() => {
    const uploaded = items.filter((it) => it.status === 'uploaded').length
    const failed = items.filter((it) => it.status === 'failed').length
    const rejected = items.filter((it) => it.status === 'rejected').length
    const uploading = items.some((it) => it.status === 'uploading')
    // Total counts only uploadable files; rejected rows are shown but excluded
    // so "X of Y uploaded" and the completion check never stall on them.
    return { uploaded, failed, rejected, total: items.length - rejected, isUploading: uploading }
  }, [items])

  return {
    items,
    addFiles,
    rename,
    remove,
    reset,
    uploadAll,
    retry,
    uploadedCount: counts.uploaded,
    failedCount: counts.failed,
    rejectedCount: counts.rejected,
    totalCount: counts.total,
    isUploading: counts.isUploading,
  }
}
